import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";
import {
  useSEOMeta,
  generateBreadcrumbSchema,
  generateWebPageSchema,
} from "@/components/SEO";

const sectorColors: Record<string, { bg: string; border: string; text: string }> = {
  Health: { bg: "bg-emerald-600", border: "border-emerald-500", text: "text-emerald-700" },
  Education: { bg: "bg-amber-600", border: "border-amber-500", text: "text-amber-700" },
  "Economic Security": { bg: "bg-teal-600", border: "border-teal-500", text: "text-teal-700" },
  Protection: { bg: "bg-rose-600", border: "border-rose-500", text: "text-rose-700" },
};

const sectorRoutes: Record<string, string> = {
  Health: "/our-work/health",
  Education: "/our-work/education",
  "Economic Security": "/our-work/economic-security",
  Protection: "/our-work/protection",
};

export default function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [project, setProject] = useState<any>(null);
  const [relatedProjects, setRelatedProjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [galleryImages, setGalleryImages] = useState<any[]>([]);
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);

  const sectorName = project?.sector || "";
  const projectTitle = project?.title || "";

  useSEOMeta({
    title: projectTitle ? `${projectTitle} - BHODEV Project` : "Project - BHODEV Cameroon",
    description:
      project?.description ||
      "Explore BHODEV projects driving health, education, economic security, and protection across Cameroon.",
    keywords: `${sectorName}, BHODEV project, Cameroon ${sectorName}, humanitarian Cameroon, community development, ${projectTitle}, NGO project Cameroon`,
    canonicalUrl: project?.id
      ? `https://bhodevcameroon.org/projects/${project.id}`
      : undefined,
    ogImage: project?.image_url || undefined,
    schemaData: project
      ? {
          ...generateWebPageSchema(
            project.title,
            project.description || "",
            `https://bhodevcameroon.org/projects/${project.id}`
          ),
          ...generateBreadcrumbSchema([
            { name: "Home", url: "https://bhodevcameroon.org/" },
            { name: "Our Work", url: "https://bhodevcameroon.org/our-work" },
            { name: sectorName, url: `https://bhodevcameroon.org${sectorRoutes[sectorName] || "/our-work"}` },
            { name: project.title, url: `https://bhodevcameroon.org/projects/${project.id}` },
          ]),
          "@context": "https://schema.org",
          "@type": "NGO",
          name: project.title,
          description: project.description,
          areaServed: project.location ? { "@type": "Place", name: project.location } : undefined,
          startDate: project.start_date || undefined,
          endDate: project.end_date || undefined,
          department: project.sector || undefined,
          image: project.image_url || undefined,
        }
      : undefined,
  });

  const fetchProject = useCallback(async () => {
    if (!id) return;

    // Validate UUID format before querying
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(id)) {
      setNotFound(true);
      setLoading(false);
      return;
    }

    setLoading(true);
    const { data, error } = await supabase
      .from("projects")
      .select("*")
      .eq("id", id)
      .maybeSingle();

    if (error || !data) {
      console.error("Error fetching project:", error);
      setNotFound(true);
      setLoading(false);
      return;
    }

    setProject(data);

    // Fetch gallery images
    const { data: galleryData } = await supabase
      .from("project_images")
      .select("*")
      .eq("project_id", id)
      .order("sort_order", { ascending: true });
    setGalleryImages(galleryData || []);

    // Fetch related projects in same sector
    const { data: related } = await supabase
      .from("projects")
      .select("*")
      .eq("sector", data.sector)
      .neq("id", id)
      .order("created_at", { ascending: false })
      .limit(3);

    setRelatedProjects(related || []);
    setLoading(false);
  }, [id]);

  useEffect(() => {
    fetchProject();
  }, [fetchProject]);

  const formatDate = (d: string) => {
    if (!d) return "";
    return new Date(d).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const shareProject = () => {
    if (navigator.share) {
      navigator.share({
        title: project?.title,
        text: project?.description,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      const el = document.getElementById("share-toast");
      if (el) {
        el.classList.remove("opacity-0", "translate-y-2");
        el.classList.add("opacity-100", "translate-y-0");
        setTimeout(() => {
          el.classList.add("opacity-0", "translate-y-2");
          el.classList.remove("opacity-100", "translate-y-0");
        }, 2000);
      }
    }
  };

  const colors = project ? sectorColors[project.sector] || sectorColors.Health : sectorColors.Health;
  const sectorRoute = project ? sectorRoutes[project.sector] || "/our-work" : "/our-work";

  if (notFound) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center px-4">
            <div className="w-16 h-16 flex items-center justify-center bg-stone-100 rounded-full mx-auto mb-4">
              <i className="ri-folder-chart-line text-2xl text-stone-400"></i>
            </div>
            <h1 className="text-2xl font-saria font-bold text-stone-800 mb-2">Project Not Found</h1>
            <p className="text-stone-500 mb-6">The project you are looking for does not exist or has been removed.</p>
            <Link
              to="/our-work"
              className="inline-flex items-center gap-2 bg-primary text-white px-6 py-3 rounded-full font-semibold text-sm hover:bg-primary-dark transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              Explore Our Work
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Toast notification */}
      <div
        id="share-toast"
        className="fixed top-20 left-1/2 -translate-x-1/2 z-50 bg-stone-800 text-white px-4 py-2 rounded-xl text-sm font-medium opacity-0 translate-y-2 transition-all duration-300 pointer-events-none shadow-lg"
      >
        <i className="ri-check-line mr-1.5"></i>
        Link copied to clipboard
      </div>

      {loading && (
        <div className="flex items-center justify-center min-h-[60vh]">
          <i className="ri-loader-4-line animate-spin text-primary text-3xl"></i>
        </div>
      )}

      {!loading && project && (
        <>
          {/* Hero Cover */}
          <section className="relative h-[400px] md:h-[500px] flex items-end overflow-hidden">
            <img
              src={
                project.image_url ||
                `https://readdy.ai/api/search-image?query=$%7BencodeURIComponent%28project.sector%29%7D%20humanitarian%20project%20in%20Cameroon%20community%20development%20warm%20natural%20light%20documentary%20style%20wide%20landscape&width=1400&height=600&seq=project-detail-fallback&orientation=landscape`
              }
              alt={project.title}
              title={project.title}
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/20"></div>

            <div className="relative z-10 w-full max-w-5xl mx-auto px-4 md:px-8 pb-8 md:pb-12">
              <nav className="flex items-center gap-2 text-white/60 text-sm mb-4" aria-label="Breadcrumb">
                <Link to="/" className="hover:text-white transition-colors">Home</Link>
                <i className="ri-arrow-right-s-line"></i>
                <Link to="/our-work" className="hover:text-white transition-colors">Our Work</Link>
                <i className="ri-arrow-right-s-line"></i>
                <Link to={sectorRoute} className="hover:text-white transition-colors">{project.sector}</Link>
                <i className="ri-arrow-right-s-line"></i>
                <span className="text-white/90 truncate max-w-[200px]">{project.title}</span>
              </nav>
              <span className={`inline-block ${colors.bg} text-white text-xs font-semibold px-3 py-1.5 rounded-full mb-4`}>
                {project.sector}
              </span>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-saria font-bold text-white leading-tight mb-4">
                {project.title}
              </h1>
              {project.location && (
                <div className="flex items-center gap-2 text-white/80">
                  <i className="ri-map-pin-line text-sm"></i>
                  <span className="text-sm">{project.location}</span>
                </div>
              )}
            </div>
          </section>

          {/* Meta Bar */}
          <section className="border-b border-stone-100 bg-white">
            <div className="max-w-5xl mx-auto px-4 md:px-8 py-4 flex items-center gap-6 flex-wrap">
              {project.start_date && (
                <div className="flex items-center gap-2 text-sm text-stone-600">
                  <div className="w-8 h-8 flex items-center justify-center text-stone-400">
                    <i className="ri-calendar-line text-base"></i>
                  </div>
                  <div>
                    <p className="text-xs text-stone-400">Duration</p>
                    <p className="font-medium">
                      {project.start_date}
                      {project.end_date ? ` — ${project.end_date}` : " — Present"}
                    </p>
                  </div>
                </div>
              )}
              {project.beneficiaries && (
                <div className="flex items-center gap-2 text-sm text-stone-600">
                  <div className="w-8 h-8 flex items-center justify-center text-stone-400">
                    <i className="ri-user-heart-line text-base"></i>
                  </div>
                  <div>
                    <p className="text-xs text-stone-400">Beneficiaries</p>
                    <p className="font-medium">{project.beneficiaries}</p>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-2 text-sm text-stone-600">
                <div className="w-8 h-8 flex items-center justify-center text-stone-400">
                  <i className="ri-flag-line text-base"></i>
                </div>
                <div>
                  <p className="text-xs text-stone-400">Status</p>
                  <span
                    className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${
                      project.status === "Active"
                        ? "bg-emerald-100 text-emerald-700"
                        : project.status === "Completed"
                          ? "bg-stone-100 text-stone-600"
                          : "bg-amber-100 text-amber-700"
                    }`}
                  >
                    {project.status}
                  </span>
                </div>
              </div>
            </div>
          </section>

          {/* Project Content */}
          <section className="py-10 md:py-16 px-4 md:px-8 lg:px-12">
            <div className="max-w-4xl mx-auto">
              {/* Description */}
              {project.description && (
                <p className="text-lg md:text-xl text-stone-600 font-medium leading-relaxed mb-8 border-l-4 border-primary pl-5">
                  {project.description}
                </p>
              )}

              {/* Full Content */}
              {project.content ? (
                <div
                  className="prose-content text-stone-700 text-base md:text-lg leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: project.content }}
                />
              ) : (
                <p className="text-stone-500 text-base italic">Full project details are being prepared.</p>
              )}

              {/* Image Gallery */}
              {galleryImages.length > 0 && (
                <div className="mt-12 pt-8 border-t border-stone-100">
                  <h3 className="text-lg font-saria font-bold text-dark mb-6 flex items-center gap-2">
                    <i className="ri-gallery-line text-primary"></i>
                    Project Gallery
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {galleryImages.map((img) => (
                      <div
                        key={img.id}
                        className="relative aspect-[4/3] rounded-xl overflow-hidden cursor-pointer group bg-stone-100"
                        onClick={() => setLightboxImage(img.image_url)}
                      >
                        <img
                          src={img.image_url}
                          alt={img.caption || "Project image"}
                          title={img.caption || project.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = "none";
                          }}
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                          <i className="ri-zoom-in-line text-white opacity-0 group-hover:opacity-100 transition-opacity text-2xl drop-shadow-lg"></i>
                        </div>
                        {img.caption && (
                          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-3 py-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <p className="text-white text-xs font-medium truncate">{img.caption}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Share & Actions */}
              <div className="flex items-center justify-between mt-12 pt-8 border-t border-stone-100">
                <Link
                  to={sectorRoute}
                  className="flex items-center gap-2 text-stone-500 hover:text-stone-700 text-sm font-medium transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-left-line"></i>
                  Back to {project.sector}
                </Link>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-stone-400 mr-2">Share:</span>
                  <button
                    onClick={shareProject}
                    className="w-10 h-10 flex items-center justify-center text-stone-400 hover:text-primary hover:bg-primary/10 rounded-full transition-all cursor-pointer"
                    title="Share project"
                  >
                    <i className="ri-share-line text-lg"></i>
                  </button>
                  <button
                    onClick={() => {
                      const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(project.title)}&url=${encodeURIComponent(window.location.href)}`;
                      window.open(url, "_blank");
                    }}
                    className="w-10 h-10 flex items-center justify-center text-stone-400 hover:text-stone-800 hover:bg-stone-100 rounded-full transition-all cursor-pointer"
                    title="Share on X"
                  >
                    <i className="ri-twitter-x-line text-lg"></i>
                  </button>
                  <button
                    onClick={() => {
                      const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`;
                      window.open(url, "_blank");
                    }}
                    className="w-10 h-10 flex items-center justify-center text-stone-400 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-all cursor-pointer"
                    title="Share on Facebook"
                  >
                    <i className="ri-facebook-fill text-lg"></i>
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* Related Projects */}
          {relatedProjects.length > 0 && (
            <section className="py-14 md:py-20 px-4 md:px-8 lg:px-12 bg-stone-50">
              <div className="max-w-6xl mx-auto">
                <h2 className="text-2xl md:text-3xl font-saria font-bold text-dark mb-10">
                  More {project.sector} Projects
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                  {relatedProjects.map((p) => (
                    <Link
                      key={p.id}
                      to={`/projects/${p.id}`}
                      className="group bg-white rounded-2xl overflow-hidden cursor-pointer hover:shadow-xl transition-all duration-500 border border-stone-100"
                    >
                      <div className="relative h-[200px] overflow-hidden">
                        <img
                          src={
                            p.image_url ||
                            `https://readdy.ai/api/search-image?query=$%7BencodeURIComponent%28p.sector%29%7D%20humanitarian%20project%20in%20Cameroon%20warm%20natural%20light%20documentary%20style&width=800&height=500&seq=related-proj-${p.id}&orientation=landscape`
                          }
                          alt={p.title}
                          title={p.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                        />
                        <div className="absolute top-4 left-4">
                          <span
                            className={`bg-white/90 backdrop-blur-sm text-xs px-3 py-1.5 rounded-full font-inter font-semibold shadow-sm ${colors.text}`}
                          >
                            {p.status}
                          </span>
                        </div>
                      </div>
                      <div className="p-5">
                        <h3 className="text-base font-saria font-bold text-dark leading-snug mb-2 group-hover:text-primary transition-colors duration-300">
                          {p.title}
                        </h3>
                        <p className="text-sm text-stone-500 leading-relaxed line-clamp-2">
                          {p.description || ""}
                        </p>
                        {p.location && (
                          <p className="text-xs text-stone-400 mt-2 flex items-center gap-1">
                            <i className="ri-map-pin-line"></i>
                            {p.location}
                          </p>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* CTA */}
          <section className="py-14 md:py-20 px-4 md:px-8 lg:px-12 bg-primary/5">
            <div className="max-w-2xl mx-auto text-center">
              <div className="w-14 h-14 flex items-center justify-center bg-primary/10 rounded-2xl mx-auto mb-6">
                <i className="ri-hand-heart-line text-2xl text-primary"></i>
              </div>
              <h2 className="text-2xl md:text-3xl font-saria font-bold text-dark mb-4">
                Want to Support This Project?
              </h2>
              <p className="text-stone-600 text-base md:text-lg max-w-lg mx-auto mb-8 leading-relaxed">
                Your contribution can help us expand this initiative and reach even more communities in need.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/get-involved"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-primary text-white px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-primary-dark transition-colors cursor-pointer shadow-lg shadow-primary/20"
                >
                  Get Involved
                  <i className="ri-arrow-right-line"></i>
                </Link>
                <Link
                  to="/contact"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white text-primary border border-primary/20 px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-primary/5 transition-colors cursor-pointer"
                >
                  Contact Us
                  <i className="ri-mail-line"></i>
                </Link>
              </div>
            </div>
          </section>
        </>
      )}

      <Footer />

      {/* Lightbox overlay */}
      {lightboxImage && (
        <div
          className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4 md:p-8"
          onClick={() => setLightboxImage(null)}
        >
          <button
            className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-full transition-all cursor-pointer"
            onClick={() => setLightboxImage(null)}
          >
            <i className="ri-close-line text-xl"></i>
          </button>
          <img
            src={lightboxImage}
            alt="Full size"
            className="max-w-full max-h-[85vh] rounded-xl object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}